import { Button, Card, Input } from "@app/ui";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useI18n } from "../lib/useI18n.js";
import { useAuthStore } from "../stores/index.js";

/** Public landing page for the password-reset email link (`/reset-password?token=...`).
 *  Rendered outside the app Shell; the user isn't signed in here. */
export function ResetPasswordRoute() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const resetPassword = useAuthStore((s) => s.resetPassword);
  const busy = useAuthStore((s) => s.busy);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = token && password.length >= 8 && password === confirm && !busy;

  async function submit() {
    if (!canSubmit) return;
    const ok = await resetPassword(token, password);
    if (ok) setDone(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm p-6">
        <h1 className="mb-2 text-lg font-medium">{t("resetPassword.title")}</h1>

        {!token ? (
          <p className="text-sm text-destructive">{t("resetPassword.missingToken")}</p>
        ) : done ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">{t("resetPassword.done")}</p>
            <Button onClick={() => navigate("/")}>{t("resetPassword.openApp")}</Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("resetPassword.newPassword")}
            />
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              placeholder={t("resetPassword.confirmPassword")}
            />
            {mismatch ? <span className="text-xs text-destructive">{t("resetPassword.mismatch")}</span> : null}
            <Button type="submit" disabled={!canSubmit}>
              {busy ? "..." : t("resetPassword.submit")}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
